import React, { useContext, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import AuthContext from '../../../context/authContext';
import useInternalBooking from '../../../hooks/useInternalBooking';
import { isNoShowActionable } from '../../../util/dateUtils';

function MarkNoShowButton({
  booking,
  onBookingUpdated,
  variant = 'contained',
  size = 'small',
  fullWidth = false,
  sx = undefined,
}) {
  const authCtx = useContext(AuthContext);
  const { markNoShow } = useInternalBooking();
  const [open, setOpen] = useState(false);
  const [contactOutcome, setContactOutcome] = useState('reachedConfirmedAbsent');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canShow = useMemo(() => {
    if (!booking?._id || typeof onBookingUpdated !== 'function') return false;
    if (!authCtx.checkRightAny('internalBooking:special:markNoShow')) return false;
    return isNoShowActionable(booking);
  }, [authCtx, booking, onBookingUpdated]);

  if (!canShow) return null;

  const handleOpen = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setOpen(true);
  };

  const handleClose = event => {
    event?.stopPropagation?.();
    if (isSubmitting) return;
    setOpen(false);
  };

  const handleSubmit = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id) return;

    try {
      setIsSubmitting(true);
      const response = await markNoShow(booking._id, contactOutcome);
      if (response?.booking) {
        onBookingUpdated(booking._id, response.booking);
      }
      setOpen(false);
    } catch (_) {
      // Error toasts are handled by useHttp/useInternalBooking.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        color='error'
        fullWidth={fullWidth}
        onClick={handleOpen}
        data-ignore-card-click
        sx={sx}
      >
        Gruppe nicht erschienen
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
        <DialogTitle>Gruppe nicht erschienen?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              Bitte bestätige, wie der Kontaktversuch ausgegangen ist. Danach wird die Buchung als
              No-Show markiert.
            </Typography>
            <RadioGroup
              value={contactOutcome}
              onChange={event => setContactOutcome(event.target.value)}
            >
              <FormControlLabel
                value='reachedConfirmedAbsent'
                control={<Radio />}
                label='Hab sie erreicht, sie sagen sie kommen nicht'
              />
              <FormControlLabel
                value='unreachableMarkedAbsent'
                control={<Radio />}
                label='Nicht erreichbar, aber trotzdem nicht erschienen'
              />
            </RadioGroup>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button variant='contained' color='error' onClick={handleSubmit} disabled={isSubmitting}>
            No-Show setzen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default MarkNoShowButton;
